import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Skeleton } from '@/components/Admin/UI/Skeleton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
   listWorkspacesApi,
   listMyInvitesApi,
   acceptTeamInviteByIdApi,
   declineTeamInviteApi,
   type Workspace,
   type PendingInvite,
} from '@/api/team';
import { useTeamStore, reloadIntoAccount, type ActingAccount } from '@/store/useTeamStore';
import { useSurfaceStore } from '@/store/useSurfaceStore';
import useAuthStore from '@/store/authStore';
import type { DropdownActionType } from '@/types/shared';

/**
 * "Switch to" rows for the PROFILE MENU (Ray 2026-10-05): "My account" plus
 * the email of every owner who shared with me. Picking one opens that
 * owner's whole Piso WiFi dashboard. Switching reloads the page so no data
 * from the other account stays on screen. No rows when nobody shared.
 */
export function useSwitchToActions(): DropdownActionType<null>[] {
   const acting = useTeamStore((s) => s.acting);
   const setActing = useTeamStore((s) => s.setActing);
   const setSurface = useSurfaceStore((s) => s.setSurface);
   const { data } = useQuery({
      queryKey: ['team-workspaces'],
      queryFn: listWorkspacesApi,
      staleTime: 60_000,
      // While helping, /team/workspaces is MY account (sent without X-Act-As).
   });
   const workspaces: Workspace[] = data?.workspaces ?? [];
   if (workspaces.length === 0 && !acting) return [];

   const choose = (w: Workspace | null) => {
      if (w) return openWorkspace(w);
      const next: ActingAccount | null = null;
      setActing(next);
      reloadIntoAccount('My account');
   };

   const mark = (on: boolean) => (on ? '✓ ' : '');
   return [
      {
         icon: 'user-line',
         label: `${mark(!acting)}Switch to: My account`,
         onClick: () => choose(null),
      },
      ...workspaces.map((w) => ({
         icon: 'team-line',
         label: `${mark(acting?.ownerId === w.ownerId)}Switch to: ${w.ownerEmail}`,
         onClick: () => choose(w),
      })),
   ];
}

/** Figma-style avatar row next to "Share" (Ray 2026-10-15): my own initial,
 *  then one circle per account shared with me. Click = switch; the account
 *  you are in has the ring. Nothing shows while nobody has shared with me. */
const AVATAR_HUES = ['#4f63f6', '#0f9d58', '#d96a00', '#8e44ad', '#c0392b', '#0b7285'];
const hue = (key: string) => AVATAR_HUES[[...key].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_HUES.length];
const initial = (s: string) => (s.trim().charAt(0) || '?').toUpperCase();

export const AccountStack: React.FC = () => {
   const acting = useTeamStore((s) => s.acting);
   const setActing = useTeamStore((s) => s.setActing);
   const user = useAuthStore((s) => s.user);
   // A phone header has no room for the shared faces: they squeezed the product
   // switcher beside them down to nothing (Ray 2026-11-02, 11-03). Mine + count.
   const faces = useIsMobile(768) ? 0 : 3;
   const [open, setOpen] = React.useState(false);
   const [q, setQ] = React.useState('');
   const [range, setRange] = React.useState<SharedRange>('all');
   const [openVendos, setOpenVendos] = React.useState<Set<string>>(new Set());
   // #79 (Ray 2026-10-18): SmartFi admins/staff get a second tab, FLEET — any
   // operator's account, searched on the server, opened as "SmartFi support".
   const isStaff = user?.role === 'admin' || user?.role === 'staff';
   const [tab, setTab] = React.useState<'shared' | 'fleet'>('shared');
   const [fleetQ, setFleetQ] = React.useState('');
   React.useEffect(() => {
      const t = setTimeout(() => setFleetQ(q.trim()), 250);
      return () => clearTimeout(t);
   }, [q]);
   const { data } = useQuery({ queryKey: ['team-workspaces'], queryFn: () => listWorkspacesApi(), staleTime: 60_000 });
   const fleetBounds = rangeBounds(range);
   const { data: fleetData, isFetching: fleetLoading, error: fleetError, refetch: fleetRefetch } = useQuery({
      queryKey: ['team-workspaces-fleet', fleetQ, range],
      // A date shortcut = when the operator registered; it lists every one in that period.
      queryFn: () => listWorkspacesApi({ scope: 'fleet', q: fleetQ || undefined, limit: fleetBounds ? 200 : 20, ...(fleetBounds ?? {}) }),
      enabled: isStaff && open && tab === 'fleet',
      staleTime: 30_000,
   });
   const mockN = devMockAccountCount();
   const workspaces: Workspace[] = mockN ? [...mockWorkspaces(mockN), ...(data?.workspaces ?? [])] : data?.workspaces ?? [];
   if (workspaces.length === 0 && !acting && !isStaff) return null;
   // A seller is shared every buyer (~100 a month, Ray 2026-10-18): the server
   // sends newest first; show the newest 10 and let search / dates reach the rest.
   const shared = pickAccounts(workspaces, q, acting?.ownerId, range);
   const fleetRows: Workspace[] = fleetData?.workspaces ?? [];
   const rows = tab === 'fleet' ? fleetRows : shared.rows;
   const matchedVendo = tab === 'fleet' ? new Map<string, { name: string; deviceId: string }>() : shared.matchedVendo;
   const closeDialog = () => {
      setOpen(false);
      setQ('');
      setRange('all');
      setTab('shared');
      setOpenVendos(new Set());
   };
   const toggleVendos = (id: string) =>
      setOpenVendos((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   const mine = user?.name || user?.email || 'Me';
   const backToMine = () => {
      setActing(null);
      reloadIntoAccount('My account');
   };
   return (
      <>
         <Stack type="button" aria-label="Accounts you can open" title="Accounts you can open" onClick={() => setOpen(true)}>
            <Avatar as="span" $bg={hue(mine)} $on={!acting}>{initial(mine)}</Avatar>
            {workspaces.slice(0, faces).map((w) => (
               <Avatar as="span" key={w.ownerId} $bg={hue(w.ownerEmail)} $on={acting?.ownerId === w.ownerId}>
                  {initial(w.ownerName || w.ownerEmail)}
               </Avatar>
            ))}
            {workspaces.length > faces && <Avatar as="span" $bg="#9aa2b8" $on={false}>+{workspaces.length - faces}</Avatar>}
         </Stack>

         {open && (
            <AcctOverlay>
               <AcctDialog role="dialog" aria-label="Access accounts" $fixed={workspaces.length > 6}>
                  <AcctHead>
                     <AcctTitle>Access accounts</AcctTitle>
                     <AcctClose type="button" aria-label="Close" onClick={closeDialog}>×</AcctClose>
                  </AcctHead>
                  {isStaff && (
                     <AcctTabs role="tablist" aria-label="Account lists">
                        <AcctTab type="button" role="tab" aria-selected={tab === 'shared'} $on={tab === 'shared'} onClick={() => setTab('shared')}>
                           Shared with me{workspaces.length ? ` (${workspaces.length})` : ''}
                        </AcctTab>
                        <AcctTab type="button" role="tab" aria-selected={tab === 'fleet'} $on={tab === 'fleet'} onClick={() => setTab('fleet')}>
                           Fleet
                        </AcctTab>
                     </AcctTabs>
                  )}
                  {(workspaces.length > 0 || tab === 'fleet') && (
                     <AcctSearch
                        type="search"
                        aria-label="Search accounts"
                        placeholder={tab === 'fleet' ? 'Search any operator: name, email, vendo name or device ID' : 'Search name, email, vendo name or device ID'}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                     />
                  )}
                  {(tab === 'fleet' || workspaces.length > 0) && (
                     <RangeChips role="radiogroup" aria-label={tab === 'fleet' ? 'Registered' : 'Shared'}>
                        {SHARED_RANGES.map(([v, label]) => (
                           <RangeChip key={v} type="button" role="radio" aria-checked={range === v} $on={range === v} onClick={() => setRange(v)}>
                              {label}
                           </RangeChip>
                        ))}
                     </RangeChips>
                  )}
                  <AcctRow>
                     <Avatar as="span" $bg={hue(mine)} $on={!acting}>{initial(mine)}</Avatar>
                     <AcctMain>
                        <AcctName>{mine}</AcctName>
                        <AcctMeta>{user?.email} · My account</AcctMeta>
                     </AcctMain>
                     {acting ? (
                        <AcctSwitch type="button" onClick={backToMine}>Switch</AcctSwitch>
                     ) : (
                        <AcctHere>You're here</AcctHere>
                     )}
                  </AcctRow>
                  <AcctList data-testid="acct-list">
                     {rows.map((w) => {
                        const on = acting?.ownerId === w.ownerId;
                        const vendo = matchedVendo.get(w.ownerId);
                        const vendos = w.devices ?? [];
                        const showVendos = openVendos.has(w.ownerId);
                        return (
                           <AcctItem key={w.ownerId}>
                           <AcctRow>
                              <Avatar as="span" $bg={hue(w.ownerEmail)} $on={on}>{initial(w.ownerName || w.ownerEmail)}</Avatar>
                              <AcctMain>
                                 <AcctName>{w.ownerName || w.ownerEmail}</AcctName>
                                 <AcctMeta>{w.ownerEmail} · {w.accessSummary}</AcctMeta>
                                 {vendo && <AcctVendo>Vendo: {vendo.name} · {vendo.deviceId}</AcctVendo>}
                                 {vendos.length > 0 && (
                                    <VendoToggle
                                       type="button"
                                       aria-expanded={showVendos}
                                       aria-label={`${showVendos ? 'Hide' : 'Show'} vendos of ${w.ownerEmail}`}
                                       onClick={() => toggleVendos(w.ownerId)}
                                    >
                                       {vendos.length} vendo{vendos.length === 1 ? '' : 's'} {showVendos ? '▴' : '▾'}
                                    </VendoToggle>
                                 )}
                              </AcctMain>
                              {on ? (
                                 <AcctHere>You're here</AcctHere>
                              ) : (
                                 <AcctSwitch type="button" aria-label={`Switch to ${w.ownerEmail}`} onClick={() => openWorkspace(w)}>
                                    Switch
                                 </AcctSwitch>
                              )}
                           </AcctRow>
                           {showVendos && (
                              <VendoList data-testid={`acct-vendos-${w.ownerId}`}>
                                 {vendos.map((dv) => (
                                    <li key={dv.deviceId}>
                                       <span>{dv.name}</span>
                                       <code>{dv.deviceId}</code>
                                    </li>
                                 ))}
                              </VendoList>
                           )}
                           </AcctItem>
                        );
                     })}
                     {rows.length === 0 && tab === 'fleet' && (
                        <AcctNote role={fleetError ? 'alert' : undefined}>
                           {fleetError ? (
                              <>
                                 Could not load the fleet list: {(fleetError as any)?.response?.data?.error || (fleetError as any)?.message || 'no answer from the server'}.{' '}
                                 <AcctLink type="button" onClick={() => fleetRefetch()}>Try again</AcctLink>
                              </>
                           ) : fleetLoading ? (
                              <AcctSkeleton />
                           ) : q.trim() ? (
                              `No operator matches "${q.trim()}".`
                           ) : (
                              'No operators yet.'
                           )}
                        </AcctNote>
                     )}
                     {rows.length === 0 && tab === 'shared' && (
                        <AcctNote>{!data ? <AcctSkeleton /> : q.trim() ? `No account matches "${q.trim()}".` : 'No account was shared with you in this period.'}</AcctNote>
                     )}
                  </AcctList>
                  {tab === 'fleet' && !q.trim() && fleetRows.length >= 20 && (
                     <AcctNote data-testid="acct-fleet-more">Showing the 20 newest operators — search to find any other.</AcctNote>
                  )}
                  {tab === 'shared' && !q.trim() && range === 'all' && workspaces.length > ACCOUNTS_SHOWN && (
                     <AcctNote data-testid="acct-more">
                        Showing the {ACCOUNTS_SHOWN} newest of {workspaces.length} — search to find the others.
                     </AcctNote>
                  )}
               </AcctDialog>
            </AcctOverlay>
         )}
      </>
   );
};

export const ACCOUNTS_SHOWN = 10;

/** Date shortcuts on when an account was shared with me (Ray 2026-10-18). */
export type SharedRange = 'all' | 'today' | '7d' | 'month' | 'lastMonth';
export const SHARED_RANGES: [SharedRange, string][] = [
   ['all', 'All'],
   ['today', 'Today'],
   ['7d', 'Last 7 days'],
   ['month', 'This month'],
   ['lastMonth', 'Last month'],
];
export function sharedIn(sharedAt: string | undefined, range: SharedRange, now = new Date()): boolean {
   if (range === 'all') return true;
   if (!sharedAt) return false;
   const t = new Date(sharedAt).getTime();
   const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
   const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
   if (range === 'today') return t >= startOfToday;
   if (range === '7d') return t >= startOfToday - 6 * 86_400_000;
   if (range === 'month') return t >= startOfMonth;
   const startOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
   return t >= startOfLast && t < startOfMonth;
}

/** LOCAL PREVIEW ONLY (dev server on Ray's Mac): 50 fake shared accounts are
 *  ALWAYS added so the window can be seen full (`?mockAccounts=0` turns them
 *  off). Never in a real build — import.meta.env.DEV is false there. */
function devMockAccountCount(): number {
   if (!import.meta.env.DEV || import.meta.env.MODE === 'test' || typeof window === 'undefined') return 0;
   try {
      const fromUrl = new URLSearchParams(window.location.search).get('mockAccounts');
      if (fromUrl !== null) sessionStorage.setItem('smartfi-mock-accounts', fromUrl);
      const saved = sessionStorage.getItem('smartfi-mock-accounts');
      return Math.min(500, saved === null ? 50 : Number(saved) || 0);
   } catch {
      return 50;
   }
}
export function mockWorkspaces(n: number, now = Date.now()): Workspace[] {
   return Array.from({ length: n }, (_, i) => {
      const k = String(i + 1).padStart(2, '0');
      return {
         ownerId: `mock-${k}`,
         ownerName: `MOCK Buyer ${k}`,
         ownerEmail: `mock.buyer.${k}@smartfi.test`,
         access: 'full',
         pages: [],
         switches: { transferCredits: false, deleteDevice: false, showMoney: false },
         accessSummary: 'Full access',
         sharedAt: new Date(now - i * 1.3 * 86_400_000).toISOString(),
         devices: Array.from({ length: (i % 3) + 1 }, (_, j) => ({
            name: `MOCK Shop ${k}${j ? `-${j + 1}` : ''}`,
            deviceId: `MCK-${String((i + 1) * 10 + j).padStart(8, '0')}`,
         })),
      } as Workspace;
   });
}

/** Which accounts the Accounts dialog lists. No search: the newest 10 (plus
 *  the one I'm in, if older). Search: every match on owner name / email or
 *  on one of their vendos' name / device ID — that vendo is shown on the row. */
/** The [from, to) bounds of a date shortcut, for the server; null = all time. */
export function rangeBounds(range: SharedRange, now = new Date()): { from: string; to?: string } | null {
   if (range === 'all') return null;
   const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
   const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
   if (range === 'today') return { from: startOfToday.toISOString() };
   if (range === '7d') return { from: new Date(startOfToday.getTime() - 6 * 86_400_000).toISOString() };
   if (range === 'month') return { from: startOfMonth.toISOString() };
   return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(), to: startOfMonth.toISOString() };
}

export function pickAccounts(workspaces: Workspace[], query: string, actingId?: string, range: SharedRange = 'all') {
   const q = query.trim().toLowerCase();
   const matchedVendo = new Map<string, { name: string; deviceId: string }>();
   if (range !== 'all') {
      // A date shortcut lists EVERY account shared in that period (no 10 cap).
      workspaces = workspaces.filter((w) => sharedIn(w.sharedAt, range));
      if (!q) return { rows: workspaces, matchedVendo };
   }
   if (!q) {
      const rows = workspaces.slice(0, ACCOUNTS_SHOWN);
      const current = workspaces.find((w) => w.ownerId === actingId);
      if (current && !rows.includes(current)) rows.push(current);
      return { rows, matchedVendo };
   }
   const has = (v?: string) => !!v && v.toLowerCase().includes(q);
   const rows = workspaces.filter((w) => {
      if (has(w.ownerName) || has(w.ownerEmail)) return true;
      const d = (w.devices ?? []).find((x) => has(x.name) || has(x.deviceId));
      if (d) matchedVendo.set(w.ownerId, d);
      return !!d;
   });
   return { rows, matchedVendo };
}

const AcctOverlay = styled.div`
   position: fixed; inset: 0; z-index: 1000;
   background: rgba(0, 0, 0, 0.45);
   display: flex; align-items: center; justify-content: center; padding: 16px;
   @media (max-width: 768px) { align-items: flex-end; padding: 0; }
`;
/* Fixed height (Ray 2026-10-18): the window keeps its size while you search;
   only the list inside scrolls. */
const AcctDialog = styled.div<{ $fixed: boolean }>`
   background: ${colors.WHITE}; color: ${colors.MAIN_DARK}; border-radius: 8px; padding: 18px; /* standard corners; theme colours for dark mode */
   width: 100%; max-width: 480px; overflow: hidden;
   /* Fixed only when the list is long enough to fill it — a short list
      gets a short window, never a tall white box. */
   height: ${({ $fixed }) => ($fixed ? 'min(600px, 85vh)' : 'auto')};
   max-height: 85vh;
   display: flex; flex-direction: column; gap: 6px;
   @media (max-width: 768px) {
      max-width: none; height: ${({ $fixed }) => ($fixed ? '75vh' : 'auto')};
      border-radius: 18px 18px 0 0;
      padding: 14px 14px calc(14px + env(safe-area-inset-bottom, 0px));
   }
`;
const AcctHead = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;`;
const AcctTitle = styled.h3`margin: 0; font-size: 17px;`;
const AcctClose = styled.button`border: 0; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #666;`;
const AcctRow = styled.div`
   display: flex; align-items: center; gap: 12px;
   padding: 10px 8px; border-radius: 10px;
   &:hover { background: ${colors.GRAY14}; }
`;
const AcctMain = styled.div`flex: 1; min-width: 0;`;
const AcctName = styled.div`font-weight: 600; font-size: 14px;`;
const AcctMeta = styled.div`font-size: 12px; color: ${colors.GRAY5}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const AcctSwitch = styled.button`
   border: 1px solid #4f63f6; background: #4f63f6; color: #fff;
   border-radius: 8px; padding: 6px 14px; font-weight: 600; font-size: 13px; cursor: pointer;
`;
const AcctSearch = styled.input`
   width: 100%; box-sizing: border-box; margin: 4px 0 2px;
   padding: 9px 12px; border: 1px solid ${colors.GRAY12}; border-radius: 8px; font-size: 14px;
   background: ${colors.WHITE}; color: ${colors.MAIN_DARK};
   &:focus { outline: none; border-color: #4f63f6; box-shadow: 0 0 0 3px rgba(79, 99, 246, .15); }
`;
const AcctList = styled.div`
   display: flex; flex-direction: column; gap: 6px;
   flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
`;
const AcctItem = styled.div`display: flex; flex-direction: column;`;
const AcctTabs = styled.div`
   display: flex; gap: 0; border-bottom: 1px solid ${colors.GRAY13}; margin: 0 0 8px;
   > * { flex: 1 1 0; }
`;
const AcctTab = styled.button<{ $on: boolean }>`
   border: 0; border-bottom: 2px solid ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   background: none; padding: 8px 6px; font-size: 13px; font-weight: 600; cursor: pointer;
   color: ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY5)};
`;
const RangeChips = styled.div`
   display: flex; gap: 6px; margin: 2px 0 4px;
   > * { flex: 1 1 0; min-width: 0; }
`;
const RangeChip = styled.button<{ $on: boolean }>`
   border: 1px solid ${({ $on }) => ($on ? '#4f63f6' : colors.GRAY12)};
   background: ${({ $on }) => ($on ? '#4f63f6' : colors.WHITE)};
   color: ${({ $on }) => ($on ? '#fff' : colors.MAIN_DARK)};
   border-radius: 999px; padding: 5px 6px; font-size: 12px; font-weight: 600; cursor: pointer;
   white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const VendoToggle = styled.button`
   border: 0; background: none; padding: 0; margin-top: 2px;
   font-size: 12px; font-weight: 600; color: #4f63f6; cursor: pointer;
`;
const VendoList = styled.ul`
   list-style: none; margin: 0 8px 6px 52px; padding: 2px 0 2px 10px;
   border-left: 2px solid ${colors.GRAY12}; /* no grey box (Ray 2026-10-18) */
   display: flex; flex-direction: column; gap: 3px;
   li { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; min-width: 0; }
   li span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
   code { font-size: 11.5px; color: ${colors.GRAY5}; flex: none; }
`;
const AcctVendo = styled.div`font-size: 12px; color: #4f63f6; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
/** Five account-shaped rows (avatar, name, email, Switch pill) while a list loads. */
const AcctSkeleton: React.FC = () => (
   <div data-testid="acct-skeleton" aria-busy="true" aria-label="Loading accounts" style={{ margin: '0 -8px' }}>
      {Array.from({ length: 5 }, (_, i) => (
         <SkelRow key={i}>
            <Skeleton $w="32px" $h="32px" $radius="50%" />
            <SkelMain>
               <Skeleton $block $w={`${44 + (i % 3) * 9}%`} $h="14px" />
               <Skeleton $block $w={`${66 + (i % 2) * 12}%`} $h="12px" />
            </SkelMain>
            <Skeleton $w="56px" $h="26px" $radius="8px" />
         </SkelRow>
      ))}
   </div>
);
const SkelRow = styled.div`display: flex; align-items: center; gap: 12px; padding: 10px 8px;`;
const SkelMain = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px;`;
const AcctLink = styled.button`border: 0; background: none; padding: 0; color: #4f63f6; font: inherit; font-weight: 600; cursor: pointer;`;
const AcctNote = styled.div`font-size: 12px; color: ${colors.GRAY5}; padding: 6px 8px;`;
const AcctHere = styled.span`font-size: 12px; font-weight: 600; color: #0f9d58; white-space: nowrap;`;

const Stack = styled.button`
   display: flex;
   align-items: center;
   background: none;
   border: 0;
   padding: 0;
   cursor: pointer;
   border-radius: 999px;
   > * + * { margin-left: -8px; }
   &:focus-visible { outline: 3px solid rgba(79, 99, 246, .35); outline-offset: 2px; }
`;
const Avatar = styled.button<{ $bg: string; $on: boolean }>`
   width: 32px;
   height: 32px;
   border-radius: 50%;
   border: 2px solid ${colors.WHITE};
   background: ${(p) => p.$bg};
   color: #fff;
   font-weight: 700;
   font-size: 13px;
   display: inline-grid;
   place-items: center;
   padding: 0;
   flex: none;
   box-shadow: ${(p) => (p.$on ? '0 0 0 2px #4f63f6' : '0 0 0 1px rgba(0,0,0,.08)')};
   opacity: ${(p) => (p.$on ? 1 : 0.85)};
`;

/** Opens an owner's account: remember who, land on their Piso WiFi dashboard. */
export function openWorkspace(w: Workspace) {
   useTeamStore.getState().setActing({
      ownerId: w.ownerId,
      ownerEmail: w.ownerEmail,
      ownerName: w.ownerName,
      access: w.access,
      pages: w.pages ?? [],
      switches: w.switches,
      support: !!w.support,
   });
   useSurfaceStore.getState().setSurface('wifi');
   reloadIntoAccount(w.ownerName || w.ownerEmail);
}

/**
 * "X shared their account with you" — shown on the helper's OWN dashboard
 * the moment an invite exists (Ray 2026-10-15: never depend on the email
 * link). Accept here, then "Open their account" switches in one click.
 */
export const PendingInviteBanner: React.FC = () => {
   const acting = useTeamStore((s) => s.acting);
   const qc = useQueryClient();
   const { data } = useQuery({
      queryKey: ['team-invites'],
      queryFn: listMyInvitesApi,
      enabled: !acting,
      staleTime: 30_000,
   });
   const [opened, setOpened] = React.useState<Record<string, Workspace>>({});
   const [busy, setBusy] = React.useState<string | null>(null);
   const invites: PendingInvite[] = data?.invites ?? [];
   if (acting || invites.length === 0) return null;

   const accept = async (inv: PendingInvite) => {
      setBusy(inv.id);
      try {
         const r = await acceptTeamInviteByIdApi(inv.id);
         setOpened((o) => ({ ...o, [inv.id]: r.workspace }));
         toast.success(`You can now help ${inv.ownerEmail}`);
         qc.invalidateQueries({ queryKey: ['team-workspaces'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error || err?.message || 'Could not accept');
      } finally {
         setBusy(null);
      }
   };
   const decline = async (inv: PendingInvite) => {
      setBusy(inv.id);
      try {
         await declineTeamInviteApi(inv.id);
         qc.invalidateQueries({ queryKey: ['team-invites'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error || err?.message || 'Could not do that');
      } finally {
         setBusy(null);
      }
   };

   return (
      <>
         {invites.map((inv) => {
            const w = opened[inv.id];
            return (
               <Invite key={inv.id} role="status">
                  <span>
                     <b>{inv.ownerName}</b> ({inv.ownerEmail}) shared their account with you — {inv.accessSummary}.
                  </span>
                  {w ? (
                     <InviteBtn type="button" $primary onClick={() => openWorkspace(w)}>
                        Open their account
                     </InviteBtn>
                  ) : (
                     <span style={{ display: 'inline-flex', gap: 8 }}>
                        <InviteBtn type="button" $primary disabled={busy === inv.id} onClick={() => accept(inv)}>
                           {busy === inv.id ? 'Accepting…' : 'Accept'}
                        </InviteBtn>
                        <InviteBtn type="button" disabled={busy === inv.id} onClick={() => decline(inv)}>
                           Not now
                        </InviteBtn>
                     </span>
                  )}
               </Invite>
            );
         })}
      </>
   );
};

const Invite = styled.div`
   margin: 16px 0 12px;
   padding: 10px 14px;
   border-radius: 10px;
   background: #eef2ff;
   color: #1e2a6b;
   border: 1px solid #c7d2fe;
   font-size: 13px;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const InviteBtn = styled.button<{ $primary?: boolean }>`
   border-radius: 8px;
   padding: 6px 12px;
   font-weight: 600;
   font-size: 13px;
   cursor: pointer;
   border: 1px solid ${(p) => (p.$primary ? '#4f63f6' : '#c7d2fe')};
   background: ${(p) => (p.$primary ? '#4f63f6' : '#fff')};
   color: ${(p) => (p.$primary ? '#fff' : '#1e2a6b')};
   &:disabled { opacity: 0.6; cursor: default; }
`;

/** The banner shown on every page while helping someone. */
export const HelpingBanner: React.FC = () => {
   const acting = useTeamStore((s) => s.acting);
   const setActing = useTeamStore((s) => s.setActing);
   if (!acting) return null;
   const back = () => {
      setActing(null);
      reloadIntoAccount('My account');
   };
   return (
      <Banner role="status">
         <span>
            {acting.support ? (
               <>You're viewing <b>{acting.ownerEmail}</b> as <b>SmartFi support</b>. Changes are logged on their Activity tab; transfers and deletes are off.</>
            ) : (
               <>You're helping <b>{acting.ownerEmail}</b>. Their account settings stay private.{acting.access === 'custom' && !acting.switches.showMoney ? ' Money amounts are hidden.' : ''}</>
            )}
         </span>
         <BackBtn type="button" onClick={back}>
            ← Switch back to my account
         </BackBtn>
      </Banner>
   );
};

const BackBtn = styled.button`
   border: 1px solid #e0b84a;
   background: #fff;
   color: #6b4e00;
   border-radius: 8px;
   padding: 6px 12px;
   font-weight: 600;
   font-size: 13px;
   cursor: pointer;
   white-space: nowrap;
`;

const Banner = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   margin: 16px 0 12px;
   padding: 10px 14px;
   border-radius: 10px;
   background: #fff7e0;
   color: #6b4e00;
   font-size: 13px;
   border: 1px solid #f2d27a;
`;
